/**
 * Configuration module for Private LLM Chat
 * All configuration values are loaded from environment variables
 * with sensible defaults for development.
 */

// Default models configuration
const DEFAULT_MODELS = [
  { id: 'gemma4:e4b',           name: 'TAMC 5.2',       multimodal: true,  imageGeneration: false },
  { id: 'flux1-schnell',        name: 'TAMC 4.5 image', multimodal: true,  imageGeneration: true  },
];

/**
 * Safely parse JSON from environment variable
 * @param {string} envVar - The environment variable value
 * @param {*} defaultValue - Default value if parsing fails
 * @returns {*} Parsed value or default
 */
const safeJsonParse = (envVar, defaultValue) => {
  if (!envVar) return defaultValue;
  try {
    return JSON.parse(envVar);
  } catch (error) {
    console.error('[Config] Failed to parse JSON from environment variable:', error.message);
    return defaultValue;
  }
};

/**
 * Get log level from environment variable
 * @returns {string} Log level (DEBUG, INFO, WARN, ERROR)
 */
const getLogLevel = () => {
  const level = process.env.REACT_APP_LOG_LEVEL?.toUpperCase();
  const validLevels = ['DEBUG', 'INFO', 'WARN', 'ERROR'];
  if (level && validLevels.includes(level)) {
    return level;
  }
  return 'INFO'; // Default log level
};

/**
 * Application configuration object
 * All values are derived from environment variables with defaults
 */
export const config = {
  // Authentication - Google OAuth
  googleClientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || '205875031165-48l50gg4poom6r1qbs1754s9bdm002c0.apps.googleusercontent.com',

  // Authentication - IdentityServer SSO (OIDC)
  identityServerUrl: process.env.REACT_APP_IDENTITY_SERVER_URL || 'https://identity.argoaip.com',
  oidcClientId: process.env.REACT_APP_OIDC_CLIENT_ID || 'virtual.analyst',
  oidcScopes: process.env.REACT_APP_OIDC_SCOPES || 'openid profile email roles offline_access',

  // API Configuration
  apiUrl: process.env.REACT_APP_API_URL || '', // Empty string uses relative URLs (proxy)

  // Model Configuration
  models: safeJsonParse(process.env.REACT_APP_MODELS, DEFAULT_MODELS),
  defaultModel: process.env.REACT_APP_DEFAULT_MODEL || 'gemma4:e4b',

  // Application Branding
  appName: process.env.REACT_APP_NAME || 'Private LLM Chat',
  appSubtitle: process.env.REACT_APP_SUBTITLE || 'Intelligence Platform',

  // Logging Configuration
  logLevel: getLogLevel(),

  // Feature Flags
  enableWebSearch: process.env.REACT_APP_ENABLE_WEB_SEARCH !== 'false', // Enabled by default

  // Storage Keys (for localStorage/sessionStorage)
  storageKeys: {
    token: 'google_token',
    userInfo: 'user_info',
    conversations: 'chat_conversations',
    activeConversation: 'active_conversation_id',
    authProvider: 'auth_provider', // 'google' or 'sso'
  },

  // UI Configuration
  ui: {
    maxMessageWidth: process.env.REACT_APP_MAX_MESSAGE_WIDTH || '768px',
    sidebarWidth: process.env.REACT_APP_SIDEBAR_WIDTH || '260px',
  },

  // Video Summarization Configuration
  videoMaxFileSizeMB: parseInt(process.env.REACT_APP_VIDEO_MAX_FILE_SIZE_MB, 10) || 200,
  videoAllowedFormats: (process.env.REACT_APP_VIDEO_ALLOWED_FORMATS || '.mp4,.avi,.mov,.mkv,.webm').split(',').map(f => f.trim()),
  videoSupportedPlatforms: process.env.REACT_APP_VIDEO_SUPPORTED_PLATFORMS || 'YouTube, Facebook, TikTok, Instagram, LinkedIn, Twitter/X, Vimeo, Telegram',

  // Deepfake Detection Configuration
  deepfakeMaxFileSizeMB: parseInt(process.env.REACT_APP_DEEPFAKE_MAX_FILE_SIZE_MB, 10) || 200,
  deepfakeMaxImageFileSizeMB: parseInt(process.env.REACT_APP_DEEPFAKE_MAX_IMAGE_FILE_SIZE_MB, 10) || 20,
  deepfakeAllowedFormats: (process.env.REACT_APP_DEEPFAKE_ALLOWED_FORMATS || '.mp4,.avi,.mov,.mkv,.webm,.jpg,.jpeg,.png,.webp,.bmp').split(',').map(f => f.trim()),
  deepfakeAllowedVideoFormats: ['.mp4', '.avi', '.mov', '.mkv', '.webm'],
  deepfakeAllowedImageFormats: ['.jpg', '.jpeg', '.png', '.webp', '.bmp'],
  deepfakeSupportedPlatforms: process.env.REACT_APP_DEEPFAKE_SUPPORTED_PLATFORMS || 'YouTube, Facebook, TikTok, Instagram, LinkedIn, Twitter/X, Vimeo, Telegram',

  // Protest Report API — frontend uses POST /grok/search/start + poll GET /grok/search/status/{id}; backend uses PROTEST_API_* (see protest_report_api.py)
  protestReportPollIntervalMs: parseInt(process.env.REACT_APP_PROTEST_REPORT_POLL_INTERVAL_MS, 10) || 8000,
  protestReportMaxWaitMs: parseInt(process.env.REACT_APP_PROTEST_REPORT_MAX_WAIT_MS, 10) || (45 * 60 * 1000),
  protestReportRegions: (process.env.REACT_APP_PROTEST_REPORT_REGIONS || 'US,CANADA,LATAM,EU,UK,MENA,AFRICA,APAC,OCEANIA')
    .split(',')
    .map((region) => region.trim())
    .filter(Boolean),
};

/**
 * Get OIDC configuration for react-oidc-context
 * @returns {Object} OIDC configuration object
 */
export const getOidcConfig = () => ({
  authority: config.identityServerUrl,
  client_id: config.oidcClientId,
  redirect_uri: `${window.location.origin}/auth/callback`,
  silent_redirect_uri: `${window.location.origin}/silent-callback.html`,
  post_logout_redirect_uri: window.location.origin,
  response_type: 'code',
  scope: config.oidcScopes,
  automaticSilentRenew: true,
  loadUserInfo: true,
});

/**
 * Validate critical configuration
 * @returns {{ isValid: boolean, errors: string[] }}
 */
export const validateConfig = () => {
  const errors = [];

  // At least one auth method should be configured
  if (!config.googleClientId && !config.identityServerUrl) {
    errors.push('No authentication method configured (neither Google nor IdentityServer)');
  }

  if (config.models.length === 0) {
    errors.push('No models configured');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export default config;

